import { fromJS } from 'immutable';
import { IN, IS_ANY_OF } from 'reporting-data/constants/operators';
import { isJourneyReport } from 'reporting-data/tsTypes/reportTypes';
import { ENUMERATION } from 'reporting-data/constants/property-types';
import { isQuickFilterEditorSchema } from 'reporting-data/tsTypes/editorTypes';
import { generateJourneyFilterId } from 'reporting-snowflake/funnel/utils/aliasing';
import { FieldSources } from 'reporting-snowflake/relational/schema/column-records';
import { getJourneyDataTypeName, getJourneyObjectTypeId, getObjectFiltering, getPropertyFilterGroup, PROPERTY_FILTER_GROUP_ID } from '../common/journeyHelpers';
import { isFilterApplied } from './quickFilterEditorUtils';
import { BaseQuickFilterEditor } from './BaseQuickFilterEditor';
import { ReportOptionsCategories } from '../../singleReportViewer/fullPage/sidebar/categorized/ReportOptionCategory';
import { jsx as _jsx } from "react/jsx-runtime";
const PROPERTY_FILTER_TYPE = 'PROPERTY';
const getFilter = (report, schema, filterValue) => {
  const propertyName = schema.property;
  const filterWithoutId = {
    field: {
      name: propertyName,
      table: getJourneyDataTypeName(report),
      source: FieldSources.TABLE,
      type: ENUMERATION
    },
    filter: {
      filterType: PROPERTY_FILTER_TYPE,
      operation: {
        propertyType: ENUMERATION,
        operator: IS_ANY_OF,
        values: schema.operator === IN ? filterValue : [filterValue]
      },
      property: propertyName
    },
    isIgnored: false
  };
  const filterId = generateJourneyFilterId(filterWithoutId);
  return Object.assign({
    id: filterId
  }, filterWithoutId);
};
const isFilterSchemaMatch = (filter, schema) => filter.filter.property === schema.property && filter.filter.operation.operator === IS_ANY_OF;
const getNewPropertyFilters = (report, schema, filterValue) => {
  const newFilter = isFilterApplied(filterValue) ? getFilter(report, schema, filterValue) : null;
  const propertyFilterGroup = getPropertyFilterGroup(report);
  const originalFilters = propertyFilterGroup ? propertyFilterGroup.filters : [];
  const filterMatch = originalFilters.find(filter => isFilterSchemaMatch(filter, schema));
  if (!filterMatch) {
    return newFilter ? originalFilters.concat(newFilter) : originalFilters;
  }
  return originalFilters.reduce((updatedFilters, filter) => {
    if (isFilterSchemaMatch(filter, schema)) {
      return newFilter ? updatedFilters.concat(newFilter) : updatedFilters;
    }
    return /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */(
      updatedFilters.concat(filter)
    );
  }, []);
};
const getNewFilterGroups = (report, schema, filterValue) => {
  const originalFiltering = getObjectFiltering(report);
  const originalFilterGroups = originalFiltering ? originalFiltering.groups : [];
  const newPropertyFilters = getNewPropertyFilters(report, schema, filterValue);
  const newPropertyFilterGroup = newPropertyFilters.length > 0 ? {
    id: PROPERTY_FILTER_GROUP_ID,
    condition: 'AND',
    filters: newPropertyFilters
  } : null;
  const propertyFilterGroup = getPropertyFilterGroup(report);
  if (!propertyFilterGroup) {
    return newPropertyFilterGroup ? originalFilterGroups.concat(newPropertyFilterGroup) : originalFilterGroups;
  }
  return originalFilterGroups.reduce((newGroups, group) => {
    if (group.id === PROPERTY_FILTER_GROUP_ID) {
      return newPropertyFilterGroup ? newGroups.concat(newPropertyFilterGroup) : newGroups;
    }
    return /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */(
      newGroups.concat(group)
    );
  }, []);
};
export const strategy = {
  isSupportedForReport: report => isJourneyReport(report),
  updateReport: (report, schema, filterValue) => {
    const newFilterGroups = getNewFilterGroups(report.toJS(), schema, filterValue);
    const filtering = newFilterGroups.length > 0 ? fromJS({
      groups: newFilterGroups,
      condition: 'AND'
    }) : null;
    return report.setIn(['journeyQuery', 'objectQuery', 'filtering'], filtering);
  },
  getDataType: report => getJourneyObjectTypeId(report.toJS()),
  getCurrentValue: (report, schema) => {
    const propertyFilterGroup = getPropertyFilterGroup(report.toJS());
    if (!propertyFilterGroup) {
      return schema.operator === IN ? [] : null;
    }
    const match = propertyFilterGroup.filters.find(filter => isFilterSchemaMatch(filter, schema));
    if (!match) {
      return schema.operator === IN ? [] : null;
    }
    const filterValues = match.filter.operation.values;
    return schema.operator === IN ? filterValues : filterValues[0];
  }
};
const JourneyQuickFilterEditorComponent = props => {
  return /*#__PURE__*/_jsx(BaseQuickFilterEditor, Object.assign({}, props, {
    strategy: strategy,
    schema: props.schema
  }));
};
export const JourneyQuickFilterEditor = {
  matchesEditorType: (report, schema) => {
    return isQuickFilterEditorSchema(schema) && strategy.isSupportedForReport(report);
  },
  EditorComponent: JourneyQuickFilterEditorComponent,
  category: ReportOptionsCategories.More
};